import { LucideExternalLink, LucideImage } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import { aHoverFeature, aSelectedListing, useAtom, useSetAtom } from '~/atoms'
import { Image, SelectionCheckbox } from '~/components/common'
import { ListingFeatured, ListingLabels } from '~/components/listing'
import { Badge, Button } from '~/components/ui'
import { MapView, useCurrentMapView } from '~/hooks/map'
import { cn, fmtAddress, getDom } from '~/lib'
import type { Listing } from '~/models'

function CardCompany({ listing }: { listing: Listing }) {
  return (
    <div className="pointer-events-auto flex items-center gap-2 px-4 pb-3">
      {listing.organization.website ? (
        <a
          rel="noreferrer"
          className="group flex items-center gap-2"
          href={`${listing.organization.website}`}
          target="_blank"
          onClick={(e) => e.stopPropagation()}>
          {listing.organization.logo && (
            <img
              className="max-h-8 max-w-14 rounded-sm object-contain"
              alt={`${listing.organization.name} Logo`}
              src={listing.organization.logo}
            />
          )}
          <span className="line-clamp-1 underline-offset-4 group-hover:underline">
            {listing.organization.name}
          </span>
        </a>
      ) : (
        <p className="group flex items-center gap-2">
          {listing.organization.logo && (
            <img
              className="w-18 h-12 object-contain"
              alt={`${listing.organization.name} Logo`}
              src={listing.organization.logo}
            />
          )}
          <span className="line-clamp-1">{listing.organization.name}</span>
        </p>
      )}
    </div>
  )
}

type MapListingCardProps = {
  className?: string
  listing: Listing
}

export default function MapListingCard({
  className,
  listing,
}: MapListingCardProps) {
  const navigate = useNavigate()
  const currentMapView = useCurrentMapView()
  const [selectedListing, setSelectedListing] = useAtom(aSelectedListing)
  const setHoverFeature = useSetAtom(aHoverFeature)

  const listingLink = useMemo(() => `/listings/${listing.slug}`, [listing.slug])
  const cardLabel = listing.name || listing.property.address || 'listing'
  const cardAddress = listing.property.address
    ? fmtAddress(listing.property, true)
    : null
  const cardAccessibleLabel =
    cardAddress && cardAddress !== cardLabel
      ? `${cardLabel} at ${cardAddress}`
      : cardLabel

  const onCardActivate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (
        currentMapView == MapView.Map &&
        e.metaKey === false &&
        e.ctrlKey === false
      ) {
        navigate(`/${listing.slug}${location.search}`)
        setSelectedListing(listing)
      } else if (e.metaKey || e.ctrlKey || currentMapView === MapView.Cards) {
        window.open(listingLink, '_blank')
      }
    },
    [currentMapView, listing, listingLink, navigate, setSelectedListing]
  )
  return (
    <div
      className={cn(
        'relative cursor-pointer rounded-md bg-background shadow-md',
        className
      )}
      onMouseOver={() => {
        if (!selectedListing && currentMapView === MapView.Map)
          setHoverFeature({
            address: listing.property.address,
            lat: listing.property.latitude,
            lng: listing.property.longitude,
            name: listing.name!,
            property_types: listing.property_types
              ? listing.property_types.join(', ')
              : '',
            status: listing.status,
            type: listing.type,
            _id: listing._id,
            card: true,
          })
      }}
      onMouseOut={() => {
        setHoverFeature(null)
      }}>
      <SelectionCheckbox listing={listing} />
      <Button
        asChild
        variant="ghost"
        size="map-sm"
        className="absolute right-2 top-2 z-30 bg-white shadow-md ring-primary ring-offset-transparent hover:bg-white hover:outline-none hover:ring-1 hover:ring-offset-1">
        <Link
          to={listingLink}
          target="_blank"
          aria-label={`Open listing ${cardAccessibleLabel} in a new tab`}
          onClick={(e) => e.stopPropagation()}>
          <LucideExternalLink className="h-4 w-4" />
        </Link>
      </Button>

      <button
        type="button"
        aria-label={`Open listing ${cardAccessibleLabel}`}
        className="focus-ring-primary absolute inset-0 z-10 rounded-md"
        onClick={onCardActivate}
      />

      <div
        className={cn(
          'pointer-events-none relative z-20 flex h-full flex-col'
        )}>
        <div className="relative aspect-video overflow-hidden rounded-t-md bg-muted">
          {(listing.images || []).length > 0 ? (
            <Image
              className="aspect-video w-full object-cover"
              src={listing.images![0].thumbnail_url}
              alt={`${cardLabel} thumbnail`}
              lazy={true}
            />
          ) : (
            <LucideImage className="absolute inset-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform text-foreground/75" />
          )}
          {getDom(listing.created) < 10 && (
            <Badge className="absolute bottom-2 right-2 bg-emerald-700 text-sm font-semibold text-white hover:bg-emerald-700">
              NEW
            </Badge>
          )}
        </div>
        <div className="flex grow flex-col items-start px-4 pb-3 pt-3">
          <h3 className="line-clamp-2 flex items-center gap-1 text-lg font-medium leading-tight">
            {listing.featured && <ListingFeatured className="h-5 w-5" />}
            <span className="line-clamp-1">
              {listing.property.address
                ? fmtAddress(listing.property, true)
                : listing.name}
            </span>
          </h3>
          {!listing.property.address && (
            <p className="text-base font-light tracking-wider text-foreground">
              {fmtAddress(listing.property, true)}
            </p>
          )}
          <ListingLabels listing={listing} />
        </div>
        <CardCompany listing={listing} />
      </div>
    </div>
  )
}
